import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Box, Typography, alpha, IconButton, Tooltip } from '@mui/material';
import { Rotate90DegreesCcw, ZoomIn, ZoomOut } from '@mui/icons-material';
import { palette } from '../../theme';

const STATUS_GLOW = {
  online: palette.success,
  offline: palette.danger,
  updating: palette.warning,
  warning: palette.warning,
};

/**
 * Interactive CSS-3D device model.
 *
 * The model is assembled from layered planes inside a preserve-3d scene, so it
 * can be rotated by dragging, zoomed with the wheel and inspected through
 * sensor hotspots without pulling in a WebGL runtime.
 */
export default function DeviceModel3D({
  status = 'online',
  deviceType = 'sensor-node',
  label,
  onHotspot,
  hotspots = [],
  height = 380,
}) {
  const [rotation, setRotation] = useState({ x: -14, y: 28 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const rotationOrigin = useRef({ x: -14, y: 28 });
  const stageRef = useRef(null);

  const accent = STATUS_GLOW[status] || palette.primary;

  const onPointerDown = useCallback((event) => {
    setDragging(true);
    dragOrigin.current = { x: event.clientX, y: event.clientY };
    rotationOrigin.current = rotation;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [rotation]);

  const onPointerMove = useCallback((event) => {
    if (!dragging) return;
    const deltaX = event.clientX - dragOrigin.current.x;
    const deltaY = event.clientY - dragOrigin.current.y;
    setRotation({
      x: Math.max(-75, Math.min(75, rotationOrigin.current.x - deltaY * 0.32)),
      y: rotationOrigin.current.y + deltaX * 0.42,
    });
  }, [dragging]);

  const onPointerUp = useCallback((event) => {
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const handler = (event) => {
      event.preventDefault();
      setZoom((current) => Math.max(0.65, Math.min(1.85, current - event.deltaY * 0.0012)));
    };
    node.addEventListener('wheel', handler, { passive: false });
    return () => node.removeEventListener('wheel', handler);
  }, []);

  const reset = () => {
    setRotation({ x: -14, y: 28 });
    setZoom(1);
  };

  const W = 168;
  const H = 210;
  const D = 46;

  const faceStyle = {
    position: 'absolute',
    width: W,
    height: H,
    borderRadius: 18,
    border: `1px solid ${alpha(accent, 0.35)}`,
    background: `linear-gradient(150deg, ${alpha('#16223F', 0.94)}, ${alpha('#080D1C', 0.96)})`,
    boxShadow: `inset 0 1px 0 ${alpha('#FFFFFF', 0.08)}, 0 24px 60px -30px ${alpha(accent, 0.9)}`,
    backfaceVisibility: 'hidden',
  };

  return (
    <Box sx={{ position: 'relative', height, width: '100%' }}>
      <Box
        ref={stageRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={reset}
        sx={{
          position: 'absolute',
          inset: 0,
          perspective: 1000,
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* ambient light pool */}
        <Box
          sx={{
            position: 'absolute',
            width: '58%',
            aspectRatio: '1',
            borderRadius: '50%',
            background: `radial-gradient(closest-side, ${alpha(accent, 0.26)}, transparent 72%)`,
            filter: 'blur(22px)',
            animation: 'holoPulse 4.2s ease-in-out infinite',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            transformStyle: 'preserve-3d',
            transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) scale(${zoom})`,
            transition: dragging ? 'none' : 'transform 320ms cubic-bezier(0.22, 0.9, 0.28, 1)',
          }}
        >
          {/* front face */}
          <Box
            sx={{
              ...faceStyle,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.4,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(80% 55% at 50% 0%, ${alpha(accent, 0.24)}, transparent 70%)`,
              }}
            />
            {/* emissive core */}
            <Box
              sx={{
                position: 'relative',
                width: 62,
                height: 62,
                borderRadius: '50%',
                background: `radial-gradient(circle at 35% 30%, ${alpha('#FFFFFF', 0.85)}, ${accent} 55%, ${alpha(accent, 0.2)} 78%)`,
                boxShadow: `0 0 30px ${alpha(accent, 0.85)}, 0 0 70px ${alpha(accent, 0.4)}`,
                animation: 'holoPulse 3s ease-in-out infinite',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                width: '62%',
                height: 3,
                borderRadius: 3,
                background: `linear-gradient(90deg, transparent, ${alpha(accent, 0.8)}, transparent)`,
              }}
            />
            <Typography
              sx={{
                position: 'relative',
                fontSize: '0.56rem',
                letterSpacing: '0.2em',
                color: alpha(accent, 0.9),
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              {deviceType}
            </Typography>
            <Typography
              className="mono"
              sx={{
                position: 'relative',
                fontSize: '0.66rem',
                color: 'text.secondary',
                letterSpacing: '0.08em',
              }}
            >
              {label}
            </Typography>

            {/* sensor hotspots */}
            {hotspots.map((hotspot, index) => (
              <Tooltip key={hotspot.id || index} title={hotspot.label}>
                <Box
                  onClick={(event) => {
                    event.stopPropagation();
                    onHotspot?.(hotspot);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: 74 + index * 34,
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    bgcolor: hotspot.active ? accent : alpha(accent, 0.45),
                    boxShadow: `0 0 14px ${accent}`,
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.35)' },
                    transition: 'transform 200ms ease',
                  }}
                />
              </Tooltip>
            ))}
          </Box>

          {/* back face */}
          <Box
            sx={{
              ...faceStyle,
              transform: `translateZ(-${D / 2}px) rotateY(180deg)`,
              background: `linear-gradient(150deg, ${alpha('#0C1428', 0.96)}, ${alpha('#060A16', 0.98)})`,
            }}
          />

          {/* side faces */}
          <Box
            sx={{
              position: 'absolute',
              width: D,
              height: H,
              left: W / 2 - D / 2,
              top: 0,
              transform: `translateZ(${W / 2}px) rotateY(90deg)`,
              transformOrigin: 'center',
              background: `linear-gradient(180deg, ${alpha('#141F3A', 0.95)}, ${alpha('#070C1B', 0.97)})`,
              border: `1px solid ${alpha(accent, 0.22)}`,
              borderRadius: 8,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: D,
              height: H,
              left: W / 2 - D / 2,
              top: 0,
              transform: `translateZ(-${W / 2}px) rotateY(-90deg)`,
              transformOrigin: 'center',
              background: `linear-gradient(180deg, ${alpha('#141F3A', 0.95)}, ${alpha('#070C1B', 0.97)})`,
              border: `1px solid ${alpha(accent, 0.22)}`,
              borderRadius: 8,
            }}
          />

          {/* top face */}
          <Box
            sx={{
              position: 'absolute',
              width: W,
              height: D,
              left: 0,
              top: H / 2 - D / 2,
              transform: `translateY(-${H / 2}px) rotateX(90deg)`,
              background: `linear-gradient(90deg, ${alpha('#18254A', 0.96)}, ${alpha('#0A1122', 0.98)})`,
              border: `1px solid ${alpha(accent, 0.25)}`,
              borderRadius: 8,
            }}
          />

          {/* base face */}
          <Box
            sx={{
              position: 'absolute',
              width: W,
              height: D,
              left: 0,
              top: H / 2 - D / 2,
              transform: `translateY(${H / 2}px) rotateX(-90deg)`,
              background: `linear-gradient(90deg, ${alpha('#0E1730', 0.96)}, ${alpha('#060A16', 0.98)})`,
              border: `1px solid ${alpha(accent, 0.2)}`,
              borderRadius: 8,
            }}
          />

          {/* antenna */}
          <Box
            sx={{
              position: 'absolute',
              width: 5,
              height: 58,
              left: W / 2 - 2.5,
              top: -56,
              transform: 'translateZ(0px)',
              background: `linear-gradient(180deg, ${alpha(accent, 0.85)}, ${alpha(accent, 0.15)})`,
              boxShadow: `0 0 14px ${alpha(accent, 0.7)}`,
              borderRadius: 4,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 13,
              height: 13,
              borderRadius: '50%',
              left: W / 2 - 6.5,
              top: -64,
              background: accent,
              boxShadow: `0 0 20px ${accent}`,
              animation: 'holoBlink 2.6s ease-in-out infinite',
            }}
          />

          {/* status ring */}
          <Box
            sx={{
              position: 'absolute',
              width: W + 52,
              height: (W + 52) / 2.4,
              left: -(52 / 2),
              top: H / 2 - (W + 52) / 4.8,
              border: `1px solid ${alpha(accent, 0.42)}`,
              borderRadius: '50%',
              transform: 'rotateX(74deg)',
              boxShadow: `0 0 26px ${alpha(accent, 0.35)}`,
              animation: 'holoSpin 9s linear infinite',
            }}
          />
        </Box>
      </Box>

      {/* controls */}
      <Box sx={{ position: 'absolute', right: 12, bottom: 12, display: 'flex', gap: 0.75 }}>
        <Tooltip title="Zoom in">
          <IconButton
            size="small"
            onClick={() => setZoom((current) => Math.min(1.85, current + 0.15))}
            sx={{
              color: 'text.secondary',
              background: alpha('#0B1224', 0.7),
              border: `1px solid ${alpha(palette.primary, 0.2)}`,
            }}
          >
            <ZoomIn sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton
            size="small"
            onClick={() => setZoom((current) => Math.max(0.65, current - 0.15))}
            sx={{
              color: 'text.secondary',
              background: alpha('#0B1224', 0.7),
              border: `1px solid ${alpha(palette.primary, 0.2)}`,
            }}
          >
            <ZoomOut sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset view">
          <IconButton
            size="small"
            onClick={reset}
            sx={{
              color: 'text.secondary',
              background: alpha('#0B1224', 0.7),
              border: `1px solid ${alpha(palette.primary, 0.2)}`,
            }}
          >
            <Rotate90DegreesCcw sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        sx={{
          position: 'absolute',
          left: 14,
          bottom: 14,
          fontSize: '0.66rem',
          color: 'text.secondary',
          letterSpacing: '0.1em',
          pointerEvents: 'none',
        }}
      >
        DRAG TO ROTATE · SCROLL TO ZOOM
      </Typography>
    </Box>
  );
}
